import React from 'react';
import { Card, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { useApp } from '../context/AppContext';
import { API } from '../lib/api';
import { toast } from '../hooks/use-toast';
import { SlidersHorizontal, Loader2 } from 'lucide-react';

const SEV = [
  { key: 'include_critical', label: 'Critical alerts', dot: 'bg-red-500', desc: 'Overdue follow-ups, sharp score drops — always recommended.' },
  { key: 'include_warning', label: 'Warning alerts', dot: 'bg-amber-500', desc: 'Weak sessions and moderate score dips.' },
  { key: 'include_info', label: 'Info alerts', dot: 'bg-brand-cyan', desc: 'Follow-ups due within 3 days and missing signed reports.' },
];

export const DigestPreferences = () => {
  const { user, refreshData } = useApp();
  const [prefs, setPrefs] = React.useState({ include_critical: true, include_warning: true, include_info: true });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    const p = user?.digest_prefs || { include_critical: true, include_warning: true, include_info: true };
    setPrefs({ include_critical: !!p.include_critical, include_warning: !!p.include_warning, include_info: !!p.include_info });
  }, [user]);

  const toggle = async (key, v) => {
    const next = { ...prefs, [key]: v };
    setPrefs(next);
    setBusy(true);
    try {
      const r = await fetch(`${API}/auth/me/digest-prefs`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: v }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'Save failed');
      toast({ title: 'Preference saved' });
      await refreshData();
    } catch (e) {
      toast({ title: 'Save failed', description: e.message });
      setPrefs(prefs); // revert
    } finally { setBusy(false); }
  };

  return (
    <Card className="border-brand-border">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <SlidersHorizontal className="h-4 w-4 text-brand-indigo" />
          <div className="text-sm font-semibold text-brand-indigo">Digest Preferences</div>
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
        </div>
        <div className="text-[11px] text-muted-foreground mb-4">Choose which alert severities appear in your Monday coaching digest.</div>
        <div className="space-y-2">
          {SEV.map((s) => (
            <div key={s.key} className="flex items-start justify-between gap-4 rounded-md border border-brand-border px-3 py-3">
              <div className="flex items-start gap-2 min-w-0">
                <span className={`h-2.5 w-2.5 rounded-full mt-1.5 ${s.dot}`} />
                <div>
                  <div className="text-sm font-medium text-brand-indigo">{s.label}</div>
                  <div className="text-[11px] text-muted-foreground">{s.desc}</div>
                </div>
              </div>
              <Switch checked={!!prefs[s.key]} onCheckedChange={(v) => toggle(s.key, v)} disabled={busy} />
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px] text-muted-foreground leading-relaxed">Changes apply to the next digest (both automated Monday send and the manual “Send now” button).</div>
      </CardContent>
    </Card>
  );
};

export default DigestPreferences;
